import { imageSrcSet, imageVariant } from "./imageVariant";
import { readSnapshotImagePaths } from "./projectsSnapshot";

/**
 * Project logos and covers: the one place that says which FILE each slot
 * draws, and the warm-up that has those files decoded before the slot mounts.
 *
 * WHY THIS EXISTS (Andrew, 2026-10-06: "the Project Selection page and
 * Dashboard are always fresh loading the images")
 * --------------------------------------------------------------------------
 * The bytes were never the problem. Measured that day: every variant is
 * `public, max-age=31536000, immutable`, and after a reload a logo and a cover
 * came back with transferSize 0 in 0 ms. What the reader saw was a cached
 * picture PAINTING late, for two reasons:
 *
 *  1. On a cold launch — and every sign-in and sign-out is a full page load
 *     now (lib/landAfterSignIn.ts) — the <img> cannot exist until the session
 *     and then `/api/projects` have answered. Only then does the browser start
 *     reading the file off disk and decoding it (a 2048px WebP is tens of ms on
 *     a phone), so the card or banner draws its placeholder first, every time.
 *  2. `loading="lazy"` on cards already on screen waits for a layout pass
 *     before it even asks for the cached file.
 *
 * So at boot, using the image paths in the device's copy of the project list
 * (lib/projectsSnapshot.ts — written whenever the projects load, cleared at
 * sign-out), the ones the opening screen will draw are fetched and decoded in
 * parallel with the session check. When the cards mount, the decoded picture
 * is already in the page's memory cache and paints in the first frame. A card
 * pressed (or hovered) in the picker warms its dashboard banner the same way.
 *
 * CHANGED IMAGES: the object key IS the version (server/lib/objectCache.ts) —
 * a new upload is a new URL. What the page draws always comes from the live
 * project row, so a changed logo or cover is drawn from its new URL the moment
 * the row arrives. A remembered path that has since been replaced only warms a
 * file nobody asks for, and the copy is rewritten whenever the projects load.
 */

type ImageAttrs = { src: string; srcSet?: string; sizes?: string; crossOrigin?: "anonymous" };

/** The project card's cover banner (tablet and up). */
export const cardCover = (src: string): ImageAttrs => ({
  src: imageVariant(src, 480),
  srcSet: imageSrcSet(src, [512, 1024, 2048]),
  sizes: "(min-width: 1280px) 480px, (min-width: 768px) 50vw, 100vw",
});

/** The dashboard's cover banner. */
export const dashboardCover = (src: string): ImageAttrs => ({
  src: imageVariant(src, 1280),
  srcSet: imageSrcSet(src, [1024, 2048]),
  sizes: "(min-width: 1280px) 1280px, 100vw",
});

/**
 * A logo in a LetterAvatar disc of `size` CSS px. Requested CORS-mode because
 * the avatar samples its pixels — and the mode is part of the memory-cache
 * key, so the warm-up must ask the same way or it warms a different entry.
 */
export const logoImage = (src: string, size: number): ImageAttrs => ({
  src: imageVariant(src, size, 3),
  crossOrigin: "anonymous",
});

/**
 * A logo on a project card's plate (components/ui/logo-plate.tsx), which crops
 * the mark out of the square it was uploaded as. The mark is often half the
 * file's width or less, so this is the 512 copy for a plate at most 168px wide
 * — a smaller one leaves the wordmark soft at 2×. CORS-mode: the plate reads
 * its pixels to find the mark.
 */
export const logoPlateImage = (src: string): ImageAttrs => ({
  src: imageVariant(src, 512, 1),
  crossOrigin: "anonymous",
});

/**
 * A builder's logo opposite the plate, which draws it as a one-tone mask of
 * its shape — read off the same 512 copy, CORS-mode for the same reason.
 */
export const builderMarkImage = (src: string): ImageAttrs => ({
  src: imageVariant(src, 512, 1),
  crossOrigin: "anonymous",
});

/*
 * Held so the decoded pictures are not collected before the page draws them.
 * Bounded: a platform admin with every project open in the picker must not
 * pin a hundred covers in memory for the life of the tab.
 */
const HELD = new Map<string, HTMLImageElement>();
const HOLD_MAX = 48;

/** Fetch (from cache, normally) and decode one image, exactly as its slot will request it. */
export function warmImage(attrs: ImageAttrs, priority: "high" | "low" = "low"): void {
  if (typeof Image === "undefined") return;
  const key = `${attrs.crossOrigin ?? ""}|${attrs.srcSet ?? attrs.src}|${attrs.sizes ?? ""}`;
  if (HELD.has(key)) return;
  const img = new Image();
  // Order matters: the mode and the candidate list must be set before `src`,
  // which starts the request.
  if (attrs.crossOrigin) img.crossOrigin = attrs.crossOrigin;
  img.decoding = "async";
  img.setAttribute("fetchpriority", priority);
  if (attrs.sizes) img.sizes = attrs.sizes;
  if (attrs.srcSet) img.srcset = attrs.srcSet;
  img.src = attrs.src;
  img.decode?.().catch(() => {
    /* a missing or undecodable file is the <img>'s own business */
  });
  HELD.set(key, img);
  if (HELD.size > HOLD_MAX) HELD.delete(HELD.keys().next().value as string);
}

type ProjectImages = { projectLogo?: string | null; projectCoverImage?: string | null };

/** A path this module will touch: same-origin, never a `data:` or `blob:` URL. */
const usable = (s: string | null | undefined): s is string => typeof s === "string" && s.startsWith("/") && !s.startsWith("//");

/**
 * The project's mark in the page header and the sidebar: the card's plate
 * (ui/logo-plate.tsx `ProjectMark`, 2026-10-23), read off the same copy the
 * card uses — so a project opened from its card finds it already decoded.
 */
function warmMark(p: ProjectImages): void {
  if (usable(p.projectLogo)) warmImage(logoPlateImage(p.projectLogo));
}

/** What the dashboard draws for a project: its banner, and its mark in the header and sidebar. */
export function warmProjectDashboard(p: ProjectImages): void {
  if (usable(p.projectCoverImage)) warmImage(dashboardCover(p.projectCoverImage), "high");
  warmMark(p);
}

/** What the picker draws for a project: both card sizes draw the cover across the card and the logo on its plate. */
function warmPickerCard(p: ProjectImages): void {
  if (usable(p.projectCoverImage)) warmImage(cardCover(p.projectCoverImage));
  if (usable(p.projectLogo)) warmImage(logoPlateImage(p.projectLogo));
}

/* ── At boot ──────────────────────────────────────────────────────────── */

/** More than fill a first screen on a big display. */
const PICKER_WARM = 24;

type Remembered = { id: string; logo: string | null; cover: string | null };

/** The device's copy of the project list (lib/projectsSnapshot.ts), in roughly the picker's order. */
function readRemembered(): Remembered[] {
  return readSnapshotImagePaths()
    .filter((p) => p.state !== "Archived")
    .sort((a, b) => Number(a.isTestProject) - Number(b.isTestProject) || a.projectName.localeCompare(b.projectName));
}

/**
 * At boot, before the session or the projects have answered: decode what the
 * opening screen is about to draw. A project route warms that project's
 * dashboard; the picker (where `/` and a sign-in land) warms its first cards.
 * The list is cleared at sign-out, so a signed-out launch warms nothing.
 * Anything else warms nothing.
 */
export function warmProjectImagesAtBoot(pathname: string = location.pathname): void {
  const rows = readRemembered();
  if (!rows.length) return;
  const toProps = (r: Remembered): ProjectImages => ({ projectLogo: r.logo, projectCoverImage: r.cover });

  const inProject = /^\/projects\/([^/]+)\//.exec(pathname)?.[1];
  if (inProject) {
    const row = rows.find((r) => r.id === inProject);
    if (row && /\/dashboard\/?$/.test(pathname)) warmProjectDashboard(toProps(row));
    else if (row) warmMark(toProps(row));
    return;
  }
  if (pathname === "/" || pathname === "/projects" || pathname === "/home") {
    for (const row of rows.slice(0, PICKER_WARM)) warmPickerCard(toProps(row));
  }
}
